import { useAsyncData, useRoute } from '#imports'
import { computed, ref, watch } from 'vue'

import { getAsyncDataKey } from '@backmarket/nuxt-module-http-v2/getAsyncDataKey'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useI18nLocale } from '@backmarket/nuxt-module-i18n/useI18nLocale'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import { insertIf } from '@backmarket/utils/collection/insertIf'

import { transformReviewsResponse } from '../utils/transformReviewsResponse'

function useFetchReviews(cursor = '') {
  const route = useRoute()
  const locale = useI18nLocale()
  const $httpFetch = useHttpFetch()

  const queryParams = computed(() => ({
    order_by: '-relevance_alt',
    ...route.query,
    ...insertIf(!!cursor, { cursor }),
    translation_locale: locale?.toString(),
  }))

  return useAsyncData(
    getAsyncDataKey('reviews-list', queryParams.value),
    () => {
      return $httpFetch('/reviews/v1/products/reviews', {
        query: queryParams.value,
      })
    },
    {
      transform: (response) => {
        return {
          ...response,
          results: transformReviewsResponse(response.results),
        }
      },
    },
  )
}

export function useReviewsList() {
  const route = useRoute()
  const logger = useLogger()

  const cursor = ref()

  const { data, error, pending, execute } = useFetchReviews(cursor.value)

  const reviewsResponse = computed(() => ({
    ...data.value,
    results: data.value?.results ?? [],
  }))

  const isReviewsFetchPending = computed(() => pending.value)

  watch(
    error,
    () => {
      if (!(error.value instanceof Error)) return

      logger.error(`[REVIEWS][${route.name?.toString()}]`, {
        error: error.value,
        owners: ['bot-squad-content-front'],
      })
    },
    { immediate: true },
  )

  watch(
    data,
    (newData) => {
      cursor.value = newData?.nextCursor
    },
    { immediate: true },
  )

  watch(
    () => route.query,
    async () => {
      await execute()
    },
  )

  return { isReviewsFetchPending, reviewsResponse }
}
